// lib/strategic-playbook/decisions.js
// ════════════════════════════════════════════════════
// DECISION LOG — localStorage helpers (v1)
// ════════════════════════════════════════════════════
// Phase 1: localStorage. No auth required. Personal use.
// Phase 2 (later): migrate to Supabase + RLS for cross-device.
// ════════════════════════════════════════════════════

const STORAGE_KEY = 'sp-decisions-v1';

export const CONVICTION_TIERS = [
  {
    id: 'probe',
    label: 'Probe',
    range: '1-2% of stakes',
    description: 'I think this might work. Modest evidence, exploratory.',
    color: 'var(--sp-sage-olive)',
  },
  {
    id: 'position',
    label: 'Position',
    range: '3-5% of stakes',
    description: 'I have a thesis with modest evidence. Worth a real allocation.',
    color: 'var(--sp-aged-brass)',
  },
  {
    id: 'real-bet',
    label: 'Real Bet',
    range: '8-15% of stakes',
    description: 'Done the work. High confidence. Multiple confirmations.',
    color: 'var(--sp-cognac)',
  },
  {
    id: 'all-in',
    label: 'All-in Conviction',
    range: '20-30% of stakes',
    description: 'Once or twice in a decade. Druckenmiller-pound-short level. Earned, not declared.',
    color: 'var(--sp-oxblood)',
  },
];

export const STATUSES = [
  { id: 'active', label: 'Active', color: 'var(--sp-aged-brass)' },
  { id: 'won', label: 'Won', color: 'var(--sp-forest-moss)' },
  { id: 'lost', label: 'Lost', color: 'var(--sp-oxblood)' },
  { id: 'mixed', label: 'Mixed Outcome', color: 'var(--sp-cognac)' },
  { id: 'cancelled', label: 'Cancelled', color: 'rgba(232, 221, 196, 0.50)' },
];

export const TIME_HORIZONS = [
  { id: 'short', label: 'Short-term', detail: 'Days to weeks' },
  { id: 'medium', label: 'Medium-term', detail: 'Months to a year' },
  { id: 'long', label: 'Long-term', detail: 'Multi-year' },
  { id: 'multi-decade', label: 'Multi-decade', detail: '10+ years' },
];

function generateId() {
  return 'd_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

function safeStorage() {
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

export function listDecisions() {
  const s = safeStorage();
  if (!s) return [];
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function getDecision(id) {
  return listDecisions().find((d) => d.id === id) || null;
}

export function createDecision(decision) {
  const s = safeStorage();
  if (!s) return null;
  const now = new Date().toISOString();
  const newDecision = {
    id: generateId(),
    createdAt: now,
    updatedAt: now,
    status: 'active',
    outcomes: [],
    ...decision,
  };
  const all = listDecisions();
  all.unshift(newDecision); // newest first
  s.setItem(STORAGE_KEY, JSON.stringify(all));
  return newDecision;
}

export function updateDecision(id, updates) {
  const s = safeStorage();
  if (!s) return null;
  const all = listDecisions();
  const idx = all.findIndex((d) => d.id === id);
  if (idx === -1) return null;
  all[idx] = {
    ...all[idx],
    ...updates,
    updatedAt: new Date().toISOString(),
  };
  s.setItem(STORAGE_KEY, JSON.stringify(all));
  return all[idx];
}

export function addOutcome(id, outcome) {
  const decision = getDecision(id);
  if (!decision) return null;
  const newOutcome = {
    id: generateId(),
    loggedAt: new Date().toISOString(),
    ...outcome,
  };
  const outcomes = [...(decision.outcomes || []), newOutcome];
  return updateDecision(id, { outcomes });
}

export function deleteDecision(id) {
  const s = safeStorage();
  if (!s) return false;
  const all = listDecisions().filter((d) => d.id !== id);
  s.setItem(STORAGE_KEY, JSON.stringify(all));
  return true;
}

// ─── Stats / aggregate calculations ─────────────────
export function getStats(decisions = null) {
  const list = decisions || listDecisions();
  const total = list.length;
  const byStatus = {};
  const byConviction = {};
  const byPillar = {};

  STATUSES.forEach((s) => {
    byStatus[s.id] = list.filter((d) => d.status === s.id).length;
  });

  CONVICTION_TIERS.forEach((c) => {
    const decisionsInTier = list.filter((d) => d.convictionTier === c.id);
    const wonInTier = decisionsInTier.filter((d) => d.status === 'won').length;
    const lostInTier = decisionsInTier.filter((d) => d.status === 'lost').length;
    const resolved = wonInTier + lostInTier;
    byConviction[c.id] = {
      total: decisionsInTier.length,
      won: wonInTier,
      lost: lostInTier,
      winRate: resolved > 0 ? (wonInTier / resolved) * 100 : null,
    };
  });

  list.forEach((d) => {
    (d.pillars || []).forEach((p) => {
      byPillar[p] = (byPillar[p] || 0) + 1;
    });
  });

  const resolvedCount = byStatus.won + byStatus.lost;
  const overallWinRate = resolvedCount > 0 ? (byStatus.won / resolvedCount) * 100 : null;

  return {
    total,
    byStatus,
    byConviction,
    byPillar,
    overallWinRate,
    resolvedCount,
  };
}

// ─── Demo data seed (for fresh users) ───────────────
export function seedDemoIfEmpty() {
  const s = safeStorage();
  if (!s) return;
  const existing = listDecisions();
  if (existing.length > 0) return; // don't seed if user has data

  const now = Date.now();
  const demo = [
    {
      id: generateId(),
      createdAt: new Date(now - 86400000 * 60).toISOString(),
      updatedAt: new Date(now - 86400000 * 30).toISOString(),
      status: 'won',
      decision: 'Sized up NVDA from 4% to 9% allocation after Q3 earnings beat showed AI demand still accelerating.',
      thesis: 'Picks-and-shovels thesis still has runway. Data center capex from hyperscalers still ramping. NVDA had clearest visibility.',
      fears: 'Buying near peak. Crowd-trade risk. Single-name concentration.',
      successCriteria: 'Hold for 18 months unless thesis breaks (hyperscaler capex pulls back, competitive moat erodes, or AI capability progress stalls).',
      failureCriteria: 'Cut to 4% if NVDA drops 25% from entry without thesis confirming. Cut to 0% if competitive position visibly erodes.',
      convictionTier: 'real-bet',
      pillars: [4, 5],
      timeHorizon: 'medium',
      outcomes: [
        {
          id: generateId(),
          loggedAt: new Date(now - 86400000 * 30).toISOString(),
          status: 'won',
          description: 'Position up 35% over 30 days. Thesis confirming with continued earnings beats.',
          lessonsLearned: 'Conviction sizing worked. The 9% allocation captured the move proportional to the thesis strength. Half-Kelly was right call.',
        },
      ],
      isDemo: true,
    },
    {
      id: generateId(),
      createdAt: new Date(now - 86400000 * 14).toISOString(),
      updatedAt: new Date(now - 86400000 * 14).toISOString(),
      status: 'active',
      decision: 'Hire senior product manager for Strategic Intelligence domain. Choosing between two final candidates: experienced ex-McKinsey consultant vs scrappy startup PM with shipped products.',
      thesis: 'Need product leadership to take Strategic Intelligence to the next stage. Internal team can build but not strategize.',
      fears: 'Wrong hire kills 6+ months and damages culture. Either candidate could be wrong. McKinsey may not move fast; startup PM may lack strategic depth.',
      successCriteria: '90-day first impression review with team. Roadmap shipped on time. Team energy + retention up.',
      failureCriteria: 'Negative team feedback at 60 days. Roadmap slipping. Communication breakdown.',
      convictionTier: 'position',
      pillars: [2, 7],
      timeHorizon: 'medium',
      outcomes: [],
      isDemo: true,
    },
  ];
  s.setItem(STORAGE_KEY, JSON.stringify(demo));
}

// ─── Export / import (for backup) ───────────────────
export function exportAll() {
  return JSON.stringify(listDecisions(), null, 2);
}

export function importAll(jsonString) {
  const s = safeStorage();
  if (!s) return false;
  try {
    const data = JSON.parse(jsonString);
    if (!Array.isArray(data)) return false;
    s.setItem(STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch {
    return false;
  }
}
